import * as Sentry from "@sentry/react";
import { Temporal } from "temporal-polyfill";
import {
    fallbackTimeZone,
    resolveDisplayZone,
    resolveInstant,
    resolveWallTime
} from "./timeZoneHotpatch";

export interface IAuctionDateTimeParts {
    // The long date, e.g. "November 15, 2026".
    date: string;
    // The time with its zone label, e.g. "1:00 PM ABT".
    time: string;
    // The clock alone, with no day period and no zone, e.g. "1:00".
    clockTime: string;
    // The day period alone, e.g. "PM". Empty in a 24-hour locale.
    dayPeriod: string;
    // The short zone shown in `time`, e.g. "ABT".
    timeZoneName: string;
    // The medium date and time with its zone label,
    // e.g. "Nov 15, 2026, 1:00 PM ABT".
    mediumDateTime: string;
    // The same to the second, e.g. "Nov 15, 2026, 1:00:05 PM ABT".
    mediumDateTimeWithSeconds: string;
    // The calendar date in the display zone, e.g. "2026-11-15".
    isoDate: string;
}

// Shape the `datetime-local` input accepts, shown to admins as a hint.
export const DateTimeLocalFormat = "yyyy-MM-dd'T'HH:mm";

const dateTimeLocalPattern = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/;

// Each distinct malformed value is reported once, so re-renders don't flood Sentry.
const reportedInvalidDates = new Set<string>();

const reportInvalidDate = (isoString: string) => {
    if (reportedInvalidDates.has(isoString)) return;
    reportedInvalidDates.add(isoString);

    // Sale dates should always be explicit-offset or Z-suffixed ISO strings from
    // the API, so this is reported as an error. The fixed fingerprint groups every
    // bad value into one Sentry issue, so it alerts once rather than per value.
    Sentry.captureException(new Error("Invalid auction date"), {
        extra: { isoString },
        fingerprint: ["invalid-auction-date"]
    });
};

// The single place the visitor's timezone is resolved. Some browsers report no
// zone when they can't map the host's to an IANA ID, so it falls back to
// Alberta time, as a zone the hotpatch doesn't recognize does.
export const visitorTimeZone = (): string =>
    Temporal.Now.timeZoneId() || fallbackTimeZone;

// Parses an explicit-offset or Z-suffixed ISO string as an absolute instant.
export const parseAuctionInstant = (
    isoString: string | undefined | null
): Temporal.Instant | null => {
    if (!isoString) return null;
    try {
        return Temporal.Instant.from(isoString);
    } catch {
        reportInvalidDate(isoString);
        return null;
    }
};

// The milliseconds from now until an API instant, negative once it has
// passed. Used for elapsed-time work such as clock offsets and timeouts,
// where no timezone is involved. An unusable instant yields no offset.
export const millisecondsUntil = (
    isoString: string | undefined | null
): number => {
    const instant = parseAuctionInstant(isoString);
    return instant ? instant.epochMilliseconds - Date.now() : Number.NaN;
};

const formatIn = (
    instant: Temporal.Instant,
    timeZone: string,
    options: Intl.DateTimeFormatOptions
): string =>
    new Intl.DateTimeFormat(undefined, { ...options, timeZone }).format(
        instant.epochMilliseconds
    );

// `formatToParts` separates the day period with a narrow no-break space where
// `format` uses an ordinary one, so normalizing it keeps joined parts
// identical to the formatted string.
const joinParts = (parts: Intl.DateTimeFormatPart[]): string =>
    parts
        .map((part) => part.value)
        .join("")
        .replace(/\u202f/g, " ");

// Formats into parts, replacing only the timezone-name part with the label
// when there is one. Localized output is never parsed.
const formatPartsWithLabel = (
    instant: Temporal.Instant,
    timeZone: string,
    label: string | undefined,
    options: Intl.DateTimeFormatOptions
): Intl.DateTimeFormatPart[] =>
    new Intl.DateTimeFormat(undefined, { ...options, timeZone })
        .formatToParts(instant.epochMilliseconds)
        .map((part) =>
            part.type === "timeZoneName" && label
                ? { ...part, value: label }
                : part
        );

const clockOptions: Intl.DateTimeFormatOptions = {
    hour: "numeric",
    minute: "2-digit"
};

const mediumDateOptions: Intl.DateTimeFormatOptions = {
    day: "numeric",
    month: "short",
    year: "numeric"
};

// Formats an absolute instant in the visitor's timezone and locale, returning
// the pieces screens display: a date (e.g. "June 15, 2026" or "15 June 2026"),
// a time (e.g. "3:30 PM MDT" or "15:30 BST"), the clock, day period and
// short zone on their own, medium date-times to the minute and to the second, and the
// calendar date. Instants a stale host would place an hour out are resolved
// through `resolveDisplayZone`, and every piece is formatted in the single
// zone it returns. The pieces are lazy, so a screen only pays for what it
// reads.
export const formatAuctionDateTime = (
    isoString: string | undefined | null,
    timeZone: string = visitorTimeZone()
): IAuctionDateTimeParts | null => {
    const instant = parseAuctionInstant(isoString);
    if (!instant) return null;

    const { timeZoneId, label } = resolveDisplayZone(instant, timeZone);

    const clockParts = () =>
        formatPartsWithLabel(instant, timeZoneId, label, clockOptions);
    const timeParts = () =>
        formatPartsWithLabel(instant, timeZoneId, label, {
            ...clockOptions,
            timeZoneName: "short"
        });

    return {
        get date() {
            return formatIn(instant, timeZoneId, {
                day: "numeric",
                month: "long",
                year: "numeric"
            });
        },
        get time() {
            return joinParts(timeParts());
        },
        get clockTime() {
            return joinParts(
                clockParts().filter((part) => part.type !== "dayPeriod")
            ).trim();
        },
        get dayPeriod() {
            return (
                clockParts().find((part) => part.type === "dayPeriod")?.value ??
                ""
            );
        },
        get timeZoneName() {
            return (
                timeParts().find((part) => part.type === "timeZoneName")
                    ?.value ?? ""
            );
        },
        get mediumDateTime() {
            return joinParts(
                formatPartsWithLabel(instant, timeZoneId, label, {
                    ...mediumDateOptions,
                    ...clockOptions,
                    timeZoneName: "short"
                })
            );
        },
        get mediumDateTimeWithSeconds() {
            return joinParts(
                formatPartsWithLabel(instant, timeZoneId, label, {
                    ...mediumDateOptions,
                    ...clockOptions,
                    second: "2-digit",
                    timeZoneName: "short"
                })
            );
        },
        get isoDate() {
            return instant
                .toZonedDateTimeISO(timeZoneId)
                .toPlainDate()
                .toString();
        }
    };
};

// Converts an absolute instant to the wall time an editable field shows.
// Resolved through the hotpatch, so a stale host's tzdata never shows it an
// hour out.
export const toWallTime = (
    instant: Temporal.Instant,
    timeZone: string = visitorTimeZone()
): Temporal.PlainDateTime => resolveWallTime(instant, timeZone);

// Converts an edited wall time back to the instant it means in the visitor's
// timezone, or `null` when the zone skips or repeats it: a clock-change wall
// time is rejected rather than silently guessed.
export const toInstant = (
    wallTime: Temporal.PlainDateTime,
    timeZone: string = visitorTimeZone()
): Temporal.Instant | null => resolveInstant(wallTime, timeZone);

// The helper text a field shows when `toInstant` rejects the wall time
// because the given zone's clock change skips or repeats it.
export const skippedOrRepeatedTimeMessage = (
    timeZone: string = visitorTimeZone()
): string =>
    `This time is skipped or repeated by a clock change in ${timeZone}. Choose another time.`;

// Formats a wall time as the `datetime-local` input value, to the minute.
export const formatDateTimeLocal = (
    wallTime: Temporal.PlainDateTime | null | undefined
): string => (wallTime ? wallTime.toString({ smallestUnit: "minute" }) : "");

// Parses a `datetime-local` input value. Partial or malformed values are user
// input rather than bad API data, so they return null without Sentry reporting.
export const parseDateTimeLocal = (
    value: string | null | undefined
): Temporal.PlainDateTime | null => {
    if (!value || !dateTimeLocalPattern.test(value)) return null;
    try {
        return Temporal.PlainDateTime.from(value).round({
            smallestUnit: "minute",
            roundingMode: "trunc"
        });
    } catch {
        return null;
    }
};
